import { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Button, Typography } from '@mui/material';

export interface ErrorBoundaryProps {
  children: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  fallback?: ReactNode;
  resetOnError?: boolean;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  static defaultProps = {
    resetOnError: true
  };

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { 
      hasError: false,
      error: undefined,
      errorInfo: undefined
    };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { 
      hasError: true,
      error 
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.setState({ errorInfo });
    this.props.onError?.(error, errorInfo);
    
    // 确保滚动条可见
    this.fixScrollbar();
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps, prevState: ErrorBoundaryState) {
    // 如果错误状态从有错误变为无错误，则修复滚动条
    if (prevState.hasError && !this.state.hasError) {
      this.fixScrollbar();
    }
  }

  // 修复滚动条可见性
  private fixScrollbar() {
    if (typeof document !== 'undefined') {
      // 修复 body 样式
      document.body.style.overflow = 'visible';
      document.body.style.paddingRight = '';
      document.documentElement.style.overflow = 'visible';
      
      // 移除 Next.js 错误遮罩可能添加的样式
      const portal = document.querySelector('next-route-announcer');
      if (portal) {
        portal.remove();
      }
    }
  }

  handleReset = (): void => {
    if (this.props.resetOnError) {
      this.setState({ 
        hasError: false,
        error: undefined,
        errorInfo: undefined 
      });
    }
  };

  render() {
    if (this.state.hasError) {
      // 如果提供了自定义 fallback UI，则使用它
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // 默认的错误 UI
      return (
        <Box 
          sx={{ 
            p: 3, 
            maxWidth: 600, 
            mx: 'auto',
            mt: 4,
            border: '1px solid',
            borderColor: 'error.main',
            borderRadius: 1,
            bgcolor: 'background.paper',
            // 确保错误边界内容可滚动
            maxHeight: 'calc(100vh - 100px)',
            overflowY: 'auto'
          }}
        >
          <Typography variant="h5" color="error" gutterBottom>
            出错了
          </Typography>
          <Typography variant="body1" paragraph>
            {this.state.error?.message || '发生了一个意外错误'}
          </Typography>
          {process.env.NODE_ENV === 'development' && (
            <>
              <Typography variant="subtitle2" gutterBottom>
                错误详情 (仅开发环境显示):
              </Typography>
              <Typography 
                variant="caption" 
                component="pre" 
                sx={{ 
                  whiteSpace: 'pre-wrap',
                  fontFamily: 'monospace',
                  bgcolor: 'background.default',
                  p: 2,
                  borderRadius: 1,
                  overflowX: 'auto',
                  mb: 2,
                  fontSize: '0.75rem',
                  maxHeight: '300px',
                  overflowY: 'auto'
                }}
              >
                {this.state.error?.stack}
              </Typography>
              {this.state.errorInfo?.componentStack && (
                <>
                  <Typography variant="subtitle2" gutterBottom>
                    组件堆栈:
                  </Typography>
                  <Typography 
                    variant="caption" 
                    component="pre" 
                    sx={{ 
                      whiteSpace: 'pre-wrap',
                      fontFamily: 'monospace',
                      bgcolor: 'background.default',
                      p: 2,
                      borderRadius: 1,
                      overflowX: 'auto',
                      fontSize: '0.7rem',
                      maxHeight: '200px',
                      overflowY: 'auto'
                    }}
                  >
                    {this.state.errorInfo.componentStack.trim()}
                  </Typography>
                </>
              )}
            </>
          )}
          <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={this.handleReset}
            >
              重试
            </Button>
            <Button 
              variant="outlined" 
              color="primary"
              onClick={() => window.location.reload()}
            >
              刷新页面
            </Button>
          </Box>
        </Box>
      );
    }

    return this.props.children;
  }
}

// 使用高阶组件包装组件
// 使用示例: 
// const SafeComponent = withErrorBoundary(YourComponent);
// <SafeComponent />
export const withErrorBoundary = <P extends object>(
  Component: React.ComponentType<P>,
  errorBoundaryProps?: Omit<ErrorBoundaryProps, 'children'>
): React.FC<P> => {
  const WrappedComponent: React.FC<P> = (props) => (
    <ErrorBoundary {...errorBoundaryProps}>
      <Component {...props} />
    </ErrorBoundary>
  );
  
  // 复制静态方法
  Object.keys(Component).forEach(key => {
    // @ts-ignore
    WrappedComponent[key] = Component[key];
  });
  
  return WrappedComponent;
};

// 默认导出一个 ErrorBoundary 实例
export default ErrorBoundary;
